import React, {
  Fragment,
  useCallback,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";

import clsx from "clsx";
import FuzzySearch from "fuzzy-search";
import { isEqual } from "lodash";

import { RowVirtualizerDynamic } from "@components/virtualized-list";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Transition,
} from "@headlessui/react";

import { createSearchHighlightedLabel, renderChevronAnimation } from "./utils";

export type OptionType<Value = unknown> = {
  label: string;
  value: Value;
  disabled?: boolean;
};

export declare class CustomSearchClass {
  constructor(searchOptions: OptionType[], keys?: string[], options?: any);
  search(input: string): OptionType[];
}

interface Props<Value> {
  className?: string;
  formatDisplayValue?: (option?: OptionType<Value>) => string;
  hideCaret?: boolean;
  icon?: ReactNode;
  isClearable?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  isSearchable?: boolean;
  label?: string | ReactElement;
  maxWidth?: string;
  name?: string;
  onChange: (value: OptionType<Value>["value"]) => void;
  groupedOptions: { label?: string; options: OptionType<Value>[] }[];
  placeholder?: string | ReactElement;
  tabIndex?: number | string;
  value: OptionType<Value>["value"];
  width?: string;
  height?: string;
  testId?: string;
  CustomSearchClass?: typeof CustomSearchClass;
  // TODO: Determine to add or delete these props
  errorMessage?: string;
  errors?: any;

  menuItemRender?: ({
    option,
    value,
    onChange,
    active,
    selected,
  }: {
    option: OptionType<Value>;
    value: OptionType<Value>["value"];
    onChange: (value: OptionType<Value>["value"]) => void;
    active: boolean;
    selected: boolean;
  }) => React.ReactElement;
  menuClassName?: string;
  menuTitle?: string;
  buttonRenderer?: ({
    open,
    displayValue,
  }: {
    open: boolean;
    displayValue?: string;
  }) => React.ReactElement;
  comparator?: (a: Value, b: Value) => boolean;
}

export type ExtendableSelectProps = Omit<Props<unknown>, "value" | "options">;

const MAX_NUMBER = 1000;

export function SelectWithGroupedOptions<Value = unknown>({
  className,
  formatDisplayValue,
  hideCaret = false,
  icon,
  isClearable = false,
  isDisabled = false,
  isLoading = false,
  isSearchable = false,
  label,
  maxWidth,
  menuClassName,
  buttonRenderer,
  menuItemRender,
  CustomSearchClass,
  menuTitle,
  name,
  onChange,
  groupedOptions,
  placeholder = "Select...",
  tabIndex,
  testId,
  value,
  width,
  height,
  comparator,
}: Props<Value>) {
  const options = groupedOptions.map(({ options }) => options).flat();
  const [search, setSearch] = useState("");

  const foundOption = options.find((option) =>
    comparator
      ? comparator(option?.value, value)
      : isEqual(option?.value, value),
  );

  const displayValue = formatDisplayValue?.(foundOption) ?? foundOption?.label;
  const getDisplayValue = (open: boolean) => {
    if (isSearchable && open) {
      return null;
    }
    return displayValue || placeholder;
  };

  const filteredGroupedOptions = groupedOptions.map(({ label, options }) => {
    const searcher = new (CustomSearchClass ?? FuzzySearch)(
      options,
      ["label"],
      { sort: true },
    );
    return {
      label,
      options: searcher.search(search) as OptionType<Value>[],
    };
  });

  const SelectRow = useCallback(
    ({ row: option }: { row: any }) => {
      return (
        <ComboboxOption
          key={JSON.stringify(option.value)}
          value={option.value}
          disabled={option.disabled}
          as={Fragment}
        >
          {({ focus, selected }) =>
            menuItemRender ? (
              menuItemRender({
                option,
                value,
                onChange,
                active: focus,
                selected,
              })
            ) : (
              <div
                className={clsx(
                  (focus || selected) && !option.disabled
                    ? "bg-cosmic-3 font-semibold"
                    : "hover:bg-cosmic-4",
                  option.disabled && "bg-gray-3 pointer-events-none",
                  "px-4 py-2 cursor-pointer text-interstellar font-normal",
                )}
                onClick={() => {
                  onChange(option.value);
                }}
                dangerouslySetInnerHTML={createSearchHighlightedLabel(
                  option.label,
                  search,
                )}
              />
            )
          }
        </ComboboxOption>
      );
    },
    [menuItemRender, onChange, search, value],
  );

  return (
    <div
      className={clsx("nb-input-container text-sm", {
        "pointer-events-none": isLoading || isDisabled,
      })}
      style={{ width, maxWidth, height }}
      tabIndex={Number(tabIndex) || undefined}
      data-testid={testId}
    >
      {label && <label className="label text-v2-primary">{label}</label>}
      <Combobox
        name={name}
        disabled={isDisabled}
        value={value}
        onChange={onChange}
      >
        {({ open }) => (
          <>
            {buttonRenderer ? (
              buttonRenderer({
                open,
                displayValue,
              })
            ) : (
              <ComboboxButton
                as="div"
                className={clsx(
                  "nb-input flex justify-between items-center bg-white cursor-pointer",
                  {
                    "border border-cosmic ring-1 ring-cosmic": open,
                    "opacity-60": isDisabled,
                  },
                  className,
                )}
              >
                <div
                  className={clsx(
                    "text-ellipsis whitespace-nowrap overflow-hidden max-w-full flex-grow text-left",
                    !displayValue && placeholder && "text-disabled",
                  )}
                >
                  <ComboboxInput
                    className={clsx("bg-transparent", {
                      "w-full": isSearchable && open,
                      "w-0": !isSearchable || !open,
                    })}
                    autoComplete="off"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.code === "Space") {
                        e.stopPropagation();
                      }
                    }}
                  />
                  {getDisplayValue(open)}
                </div>
                {isClearable && Boolean(value) && (
                  <i
                    className={clsx(
                      "fa-regular fa-sharp fa-times cursor-pointer hover:text-cosmic px-0.5 py-2",
                    )}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onChange(null as any);
                    }}
                  />
                )}
                {!hideCaret && (
                  <span
                    className={clsx(
                      { "text-cosmic+1": open },
                      "pl-2 justify-self-end",
                    )}
                  >
                    {renderChevronAnimation(open, isLoading, icon)}
                  </span>
                )}
              </ComboboxButton>
            )}
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
              afterLeave={() => setSearch("")}
            >
              <ComboboxOptions
                anchor={{ to: "bottom start", gap: 13, offset: -13 }}
                className={clsx(
                  "absolute overflow-y-auto z-10 px-0 py-1 bg-white rounded-lg elevation-1",
                  "text-sm font-sans tracking-wide w-[var(--button-width)]",
                  menuClassName ? menuClassName : "max-h-[20rem]",
                )}
              >
                {menuTitle && (
                  <div className="text-xs font-light text-gray+1 px-4 py-2">
                    {menuTitle}
                  </div>
                )}
                {filteredGroupedOptions.every(
                  ({ options }) => options.length === 0,
                ) && (
                  <div className="px-4 py-2 text-disabled">
                    NO RESULTS FOUND
                  </div>
                )}
                {filteredGroupedOptions.map(({ label, options }, index) => {
                  if (options.length === 0) {
                    return null;
                  }

                  return (
                    <div
                      key={index}
                      className="border-b border-gray-2 last:border-b-transparent py-2"
                    >
                      {label && (
                        <div className="text-xxs text-gray+1 px-4 pb-1">
                          {label}
                        </div>
                      )}
                      {options.length > MAX_NUMBER ? (
                        <div key={index} className="min-h-[19rem] h-full">
                          <RowVirtualizerDynamic
                            rows={options}
                            Row={SelectRow}
                          />
                        </div>
                      ) : (
                        options.map((option) => (
                          <ComboboxOption
                            key={JSON.stringify(option.value)}
                            value={option.value}
                            disabled={option.disabled}
                            as={Fragment}
                          >
                            {({ focus, selected }) =>
                              menuItemRender ? (
                                menuItemRender({
                                  option,
                                  value,
                                  onChange,
                                  active: focus,
                                  selected,
                                })
                              ) : (
                                <div
                                  className={clsx(
                                    (focus || selected) && !option.disabled
                                      ? "bg-cosmic-3 font-semibold"
                                      : "hover:bg-cosmic-4",
                                    option.disabled &&
                                      "bg-gray-3 pointer-events-none",
                                    "px-4 py-2 cursor-pointer text-interstellar font-normal",
                                  )}
                                  onClick={() => {
                                    onChange(option.value);
                                  }}
                                  dangerouslySetInnerHTML={createSearchHighlightedLabel(
                                    option.label,
                                    search,
                                  )}
                                />
                              )
                            }
                          </ComboboxOption>
                        ))
                      )}
                    </div>
                  );
                })}
              </ComboboxOptions>
            </Transition>
          </>
        )}
      </Combobox>
    </div>
  );
}
